import { NOTE_IMAGE_BYTES_MAX, NOTE_IMAGES_MAX } from "@fieldpath/shared";
import type { NoteImageType } from "@fieldpath/shared";

/**
 * Images in a note (Jeremy, 3 Nov 2026): pasted, dropped or picked, checked
 * against the limits, then SHRUNK in the browser before they are sent -- a
 * full-screen capture is several MB as a PNG and a few hundred KB as a WebP
 * 1600px wide, which is all a note needs to show what was on the screen.
 */

/** The longest side an image is kept at. Wide enough to read a screen's text. */
export const NOTE_IMAGE_MAX_SIDE = 1600;

/** An image waiting in the note box, shrunk and ready to post. */
export interface PendingImage {
  /** Local only, for React and for removing it. */
  readonly key: string;
  readonly contentType: NoteImageType;
  readonly blob: Blob;
  /** An object URL for the thumbnail; revoke it when the image goes. */
  readonly url: string;
}

/**
 * Why a file cannot be added, or null. Checked on the file AS PICKED, before
 * shrinking, so the 5 MB limit means what a person would expect it to.
 */
export function refusal(file: { type: string; size: number }, alreadyPending: number): string | null {
  if (alreadyPending >= NOTE_IMAGES_MAX) return `A note can carry up to ${NOTE_IMAGES_MAX} images.`;
  if (!file.type.startsWith("image/")) return "Only images can be added to a note.";
  if (file.size > NOTE_IMAGE_BYTES_MAX) return "Each image must be 5 MB or smaller.";
  return null;
}

/** The size an image is drawn at: its own, or scaled down to fit the longest side. */
export function fitWithin(width: number, height: number, maxSide = NOTE_IMAGE_MAX_SIDE): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxSide) return { width, height };
  const scale = maxSide / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.85));
}

/**
 * The image redrawn at most 1600px, as WebP -- or JPEG where the browser
 * cannot write WebP (it then hands back a PNG, which is the wrong trade for a
 * photo). Refuses an image the browser cannot open.
 */
export async function shrink(file: Blob): Promise<{ contentType: NoteImageType; blob: Blob }> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("That image could not be opened.");
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("That image could not be opened.");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  for (const type of ["image/webp", "image/jpeg"] as const) {
    const blob = await toBlob(canvas, type);
    if (blob && blob.type === type) return { contentType: type, blob };
  }
  throw new Error("That image could not be opened.");
}

/** Base64 without the "data:...;base64," prefix, as the API takes it. */
export function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(new Error("That image could not be read."));
    reader.readAsDataURL(blob);
  });
}

/** The image files in a paste or a drop, in order. */
export function imageFiles(list: DataTransferItemList | FileList | null | undefined): File[] {
  if (!list) return [];
  const out: File[] = [];
  for (const entry of Array.from(list as ArrayLike<DataTransferItem | File>)) {
    const file = "getAsFile" in entry ? (entry.kind === "file" ? entry.getAsFile() : null) : entry;
    if (file && file.type.startsWith("image/")) out.push(file);
  }
  return out;
}

